import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { CustomFormItem } from "Components/Form/CustomFormItem";
import { Row, Col, Tooltip, Tag } from "antd";
import { PriorityLabel } from "../Components/Priority";
import { qaDetailsStore } from "../Stores/QADetailsStore";
import { observer } from "mobx-react";
import { Status } from "../Components/Status";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { projectsDataStore } from "Stores";
import { QAAnswers } from "../Components/QAAnswers";
import { QACategorySelect } from "../Components/QACategorySelect";
import { QAAnswerForm } from "../Components/QAAnswerForm";
import { modalProps } from "../utils/modalProps";
import { DetailListActions, PrimaryActionsConfig, DropdownActionConfig } from "Components/General/DetailListActions";
import { QAQuestion } from "../Components/QAQuestion";
import { QA_LINK } from "../enums";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { QARejectQuestionForm } from "../Components/QARejectQuestionForm";
import { QATypeSelect } from "../Components/QATypeSelect";
import { dayjs } from "Config/dayjs";
import { WithModal } from "Components/General/Modal/withModal";
import { QANewQuestion, QANewQuestionInstanceProps } from "../Components/QANewQuestion";
import { Spin } from "Components/General/Spin";
import { Button } from "Components/General/Button";
import { getUrlParams } from "utils/getUrlParams";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { QuestionDelegatePicker } from "../Components/QuestionDelegatePicker";
import { qaAnswersStore } from "../Stores/QAAnswersStore";
import { useParams, useNavigate, useLocation } from "react-router";
import { useCallback, useEffect, useRef, useState } from "react";

interface QADetailsProps extends WithModal {}

export const QA_LINK_TO_ROLE_MAP: { [key in QA_LINK]: API_QA_ROLE } = {
  questions: "BUYER",
  "to-admission": "GATEKEEPER_BUYER",
  "to-be-accepted": "GATEKEEPER_SELLER",
  "to-answer": "SPECIALIST",
  "to-approve-1": "APPROVAL_FIRST_STAGE",
  "to-approve-2": "APPROVAL_SECOND_STAGE",
  "to-approve-category": "APPROVAL_CATEGORY",
  "all-questions": "READ_ALL",
};

const countdownViewMap: API_QUESTION_STATUS[] = [
  "ACCEPTED",
  "IN_ACCEPTANCE",
  "IN_PROGRESS",
  "ANSWER_REJECTED",
  "IN_CATEGORY_APPROVAL",
  "IN_FIRST_STAGE_APPROVAL",
  "IN_SECOND_STAGE_APPROVAL",
];

export const QADetails = observer(({ showModal }: QADetailsProps) => {
  const { t } = useTranslation([I18N_NS.QA, I18N_NS.PROJECT]);
  const params = useParams<{ filter: string; id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, category } = qaDetailsStore;
  const { qaModuleSettings = {} } = projectsDataStore.currentProject;
  const formItemProps = { className: "for-view", colon: true };
  const [countdown, setCountdown] = useState({ value: t("Loading..."), negative: null });
  const [relatedQuestion, setRelatedQuestion] = useState(() => {
    const { relatedQuestion } = getUrlParams(location.search);
    return relatedQuestion ? +relatedQuestion : undefined;
  });
  const updateCountdown = useRef(null);
  const isNotInAllQuestionsPage = params.filter !== QA_LINK.ALL_QUESTIONS;

  const handleBackLink = useCallback(() => {
    navigate("..", { relative: "path" });
  }, [navigate]);

  const handleForwardToSpecialist = useCallback(
    async (_id?: number) => {
      await qaDetailsStore.forwardToSpecialist();
      handleBackLink();
    },
    [handleBackLink]
  );

  const handleSubmit = useCallback(
    async (_id?: number) => {
      await qaDetailsStore.submit();
      handleBackLink();
    },
    [handleBackLink]
  );

  const setCountdownTimer = useCallback(() => {
    if (Object.prototype.hasOwnProperty.call(qaDetailsStore.data, "expiresIn")) {
      const limitTime = +qaDetailsStore.data.expiresIn;
      const currentTime = qaDetailsStore.data.completed ? +qaDetailsStore.data.completed : +dayjs().format("x");
      const diffTime = limitTime > currentTime ? limitTime - currentTime : currentTime - limitTime;
      const isNegative = Math.sign(limitTime - currentTime);
      const duration = dayjs.duration(diffTime);

      if (duration.asMonths() > 30) {
        setCountdown({ value: t("Over 30 days"), negative: 0 });
      } else {
        const countdown = [
          duration.days() + "",
          duration.hours() + "",
          duration.minutes() + "",
          duration.seconds() + "",
        ]
          .map((item) => {
            if (item.length <= 1) {
              return `0${item}`;
            } else {
              return item;
            }
          })
          .join(":");
        setCountdown({ value: countdown, negative: isNegative });
      }
    } else {
      setCountdown({ value: "-", negative: null });
    }
  }, []);

  const renderCountdown = useCallback(() => {
    let countdownTimer = <span>{t("Loading...")}</span>;
    switch (countdown?.negative) {
      case -1:
        countdownTimer = <span style={{ color: "#ff0000" }}>-{countdown.value}</span>;
        break;
      default:
        countdownTimer = <span>{countdown.value}</span>;
        break;
    }
    return (
      <Tooltip title={t("days:hrs:min:sec")} placement="left" align={{ offset: [-20, 0] }}>
        {countdownTimer}
      </Tooltip>
    );
  }, [countdown]);

  const handleDelete = useCallback(async () => {
    try {
      await qaDetailsStore.delete();
      handleBackLink();
      notificationSuccess(t("Success"), t("Question withdrawn"));
    } catch (error) {
      throw error;
    }
  }, [handleBackLink]);

  const handleEdit = useCallback(() => {
    const { data } = qaDetailsStore;
    showModal<QANewQuestionInstanceProps>(QANewQuestion, {
      modalProps: { ...modalProps, title: t("Edit question") },
      componentProps: {
        refreshQaDetails: fetchQaDetails,
        initialData: data,
      },
    });
  }, [showModal]);

  const renderActionButtons = useCallback(() => {
    const {
      data: { status },
    } = qaDetailsStore;
    const { qaModuleSettings = {} } = projectsDataStore.currentProject;
    const qaLink = params.filter as QA_LINK;
    // should omit action buttons on /questions page
    const isNotInQuestionsPage = qaLink !== QA_LINK.QUESTIONS;
    const isInAllQuestionsPage = qaLink === QA_LINK.ALL_QUESTIONS;

    let primaryActions: PrimaryActionsConfig[] = [
      {
        onClick: handleBackLink,
        icon: ["fas", "chevron-left"],
        label: t("Back"),
      },
    ];
    const dropdownActions: DropdownActionConfig[] = [];
    let dropdownIcon: IconProp;
    let dropdownLabel: string;

    if (relatedQuestion) {
      const redirectBackToRelatedQuestion = () => {
        navigate(location.pathname.replace(params.id, relatedQuestion.toString()));
      };
      primaryActions = [
        {
          onClick: redirectBackToRelatedQuestion,
          icon: ["fas", "chevron-left"],
          label: t("Back"),
        },
      ];
      return { primaryActions, dropdownActions, dropdownIcon, dropdownLabel };
    }

    if (!isInAllQuestionsPage) {
      if (
        // If question is in bidder gatekeeper stage
        qaDetailsStore.data?.actions?.admit &&
        isNotInQuestionsPage
      ) {
        const handleAdmit = async () => {
          await qaDetailsStore.admit();
          handleBackLink();
        };
        primaryActions.push({
          onClick: handleAdmit,
          icon: "thumbs-up",
          label: status === "IN_ADMISSION" ? t("Admit") : t("Approve"),
        });
      }
      if (
        // If question is in bidder gatekeeper stage
        qaDetailsStore.data?.actions?.rejectByBuyerGatekeeper &&
        isNotInQuestionsPage
      ) {
        primaryActions.push({
          onClick: () =>
            showModal(QARejectQuestionForm, {
              modalProps: {
                ...modalProps,
                title: t("Reject question"),
              },
              componentProps: {
                goBack: handleBackLink,
                role: QA_LINK_TO_ROLE_MAP[qaLink],
              },
            }),
          icon: "thumbs-down",
          label: t("Reject"),
        });
      }
      if (
        // If question has to be approved by general gatekeeper
        qaModuleSettings.generalGatekeeper &&
        qaDetailsStore.data?.actions?.forwardToSpecialist &&
        isNotInQuestionsPage
      ) {
        primaryActions.push({
          onClick: handleForwardToSpecialist,
          label: t("Forward to specialist"),
          icon: "thumbs-up",
        });
      }
      if (
        // If question has to be approved by general gatekeeper
        qaModuleSettings.generalGatekeeper &&
        qaDetailsStore.data?.actions?.rejectBySellerGatekeeper &&
        isNotInQuestionsPage
      ) {
        primaryActions.push({
          onClick: () =>
            showModal(QARejectQuestionForm, {
              modalProps: {
                ...modalProps,
                title: t("Reject question"),
              },
              componentProps: {
                goBack: handleBackLink,
                role: QA_LINK_TO_ROLE_MAP[qaLink],
              },
            }),
          icon: "thumbs-down",
          label: t("Reject"),
        });
      }
      if (
        qaDetailsStore.data?.actions?.directAnswer &&
        // user is in page for GATEKEEPER_SELLER
        QA_LINK_TO_ROLE_MAP[qaLink] === "GATEKEEPER_SELLER"
      ) {
        primaryActions.push({
          onClick: () =>
            showModal(QAAnswerForm, {
              modalProps: {
                ...modalProps,
                title: t("Answer directly"),
              },
              componentProps: {
                goBack: handleBackLink,
                role: QA_LINK_TO_ROLE_MAP[qaLink],
              },
            }),
          label: t("Answer directly"),
          icon: "quote-left",
        });
      }
    }
    if (
      // If question is allowed to be answered on
      (qaModuleSettings.specialist || qaModuleSettings.delegatedTo) &&
      qaDetailsStore.data?.actions?.startWork &&
      isNotInQuestionsPage
    ) {
      primaryActions.push({ onClick: qaDetailsStore.startWork, label: t("Start work"), icon: "play-circle" });
    } else if (
      // If question is allowed to be answered on
      (qaModuleSettings.specialist || qaModuleSettings.delegatedTo) &&
      qaDetailsStore.data?.actions?.answer &&
      // user is in page for SPECIALIST
      QA_LINK_TO_ROLE_MAP[qaLink] === "SPECIALIST"
    ) {
      primaryActions.push({
        onClick: () =>
          showModal(QAAnswerForm, {
            modalProps: {
              ...modalProps,
              title: t("Answer question"),
            },
            componentProps: {
              goBack: handleBackLink,
              role: QA_LINK_TO_ROLE_MAP[qaLink],
            },
          }),
        label: t("Answer"),
        icon: "quote-left",
      });
    }

    if (qaDetailsStore.data?.actions?.rejectBySpecialist || qaDetailsStore.data?.actions?.rejectByDelegated) {
      primaryActions.push({
        icon: "thumbs-down",
        label: t("Reject"),
        onClick: () =>
          showModal(QARejectQuestionForm, {
            modalProps: {
              ...modalProps,
              title: t("Reject question"),
            },
            componentProps: {
              goBack: handleBackLink,
              role: QA_LINK_TO_ROLE_MAP[qaLink],
            },
          }),
      });
    }
    if (qaDetailsStore.data.actions?.edit) {
      primaryActions.push({
        onClick: handleEdit,
        label: t("Edit"),
        icon: "edit",
      });
    }
    if (qaDetailsStore.data.actions?.withdraw) {
      primaryActions.push({
        onClick: handleDelete,
        label: t("Withdraw"),
        icon: "comment-slash",
      });
    }
    if (!isNotInQuestionsPage && qaDetailsStore.data.actions?.submit) {
      primaryActions.push({
        onClick: handleSubmit,
        label: t("Submit"),
        icon: "file-check",
        isPrimary: true,
      });
    }

    return { primaryActions, dropdownActions, dropdownIcon, dropdownLabel };
  }, [handleBackLink, handleDelete, handleEdit, handleSubmit, location.pathname, params.id, relatedQuestion]);

  const changeCategory = useCallback(
    async (id: number) => {
      await qaDetailsStore.changeCategory(id);
      notificationSuccess(t("Success"), t("Category was changed"));
    },
    [notificationSuccess]
  );

  const changeType = useCallback(
    async (isPrivate: boolean) => {
      await qaDetailsStore.changeType(isPrivate);
      notificationSuccess(t("Success"), t("Type changed"));
    },
    [notificationSuccess]
  );

  const handleCreateRelativeQuestion = useCallback(() => {
    const prefixString = t("FE");
    showModal<QANewQuestionInstanceProps>(QANewQuestion, {
      modalProps: { ...modalProps, title: t("New further enquiry", { ns: I18N_NS.QA }) },
      componentProps: {
        refreshQaDetails: fetchQaDetails,
        initialData: {
          topic: `${prefixString}: ${qaDetailsStore.data.topic}`,
          categoryId: qaDetailsStore.data.categoryId,
          parentQuestionId: qaDetailsStore.data.id,
        },
        createRelativeQuestion: true,
      },
    });
  }, [showModal]);

  const goToRelatedQuestion = useCallback(() => {
    const currentUrl = location.pathname;
    const nextUrl =
      currentUrl.replace(params.id, qaDetailsStore.data.parentQuestionId.toString()) +
      `?relatedQuestion=${qaDetailsStore.data.id}`;
    navigate(nextUrl);
  }, [navigate]);

  const fetchQaDetails = useCallback(async () => {
    await qaDetailsStore.fetch(QA_LINK_TO_ROLE_MAP[params.filter]);
    await qaDetailsStore.fetchSuggestions();
    if (countdownViewMap.includes(qaDetailsStore.data.status)) {
      updateCountdown.current = setInterval(() => {
        setCountdownTimer();
      }, 1000);
    } else {
      setCountdownTimer();
    }
  }, []);

  useEffect(() => {
    if (params.id) {
      qaAnswersStore.clear();
      qaDetailsStore.clear();
      qaDetailsStore.setQuestionId(+params.id);
      fetchQaDetails();

      const { relatedQuestion } = getUrlParams(location.search);
      setRelatedQuestion(relatedQuestion ? +relatedQuestion : undefined);
    } else {
      qaDetailsStore.setQuestionId(+params.id);
      fetchQaDetails();
    }

    return () => {
      qaDetailsStore.clear();
      clearInterval(updateCountdown.current);
    };
  }, [params.id]);

  return (
    <Spin spinning={qaDetailsStore.state === "LOADING"}>
      <div>
        <Row justify="space-between">
          <DetailListActions {...renderActionButtons()} />
          {data.furtherEnquiryAvailable && (
            <Button onClick={handleCreateRelativeQuestion} faIcon="plus" type="primary">
              {t("New question that refers to this one", { ns: I18N_NS.QA })}
            </Button>
          )}
        </Row>
        <Row gutter={20}>
          <Col xxl={{ span: 16 }} xl={{ span: 14 }} lg={{ span: 14 }}>
            <h1 style={{ fontSize: "2em" }}>{data.topic}</h1>
            {qaDetailsStore.relatedQuestionTopic && (
              <Row>
                <Tag className="mr-5">{t("Further enquiry", { ns: I18N_NS.QA })}</Tag>
                <div className="mr-5">{qaDetailsStore.relatedQuestionTopic}</div>
                <FontAwesomeIcon
                  onClick={goToRelatedQuestion}
                  className="pointer"
                  icon={["far", "link"]}
                  fixedWidth={true}
                />
              </Row>
            )}
          </Col>
          <Col xxl={{ span: 8 }} xl={{ span: 10 }} lg={{ span: 10 }} style={{ marginBottom: 20 }}>
            <CustomFormItem label={"Nr."} {...formItemProps}>
              {data.localId}
            </CustomFormItem>
            <CustomFormItem label={t("Category")} {...formItemProps}>
              {data.actions?.changeCategory ? (
                <QACategorySelect onChange={changeCategory} defaultValue={data.categoryId} />
              ) : category ? (
                category.name
              ) : null}
            </CustomFormItem>
            <CustomFormItem label={t("Type")} {...formItemProps}>
              {qaDetailsStore.data.actions?.publish ? (
                <QATypeSelect onChange={changeType} isPrivate={data.private} />
              ) : data.private ? (
                t("Private")
              ) : (
                t("Public")
              )}
            </CustomFormItem>
            <CustomFormItem label={"Prio"} {...formItemProps}>
              <PriorityLabel type={data.priority} />
            </CustomFormItem>
            {projectsDataStore.currentProject.qaModuleSettings?.timerActive && (
              <CustomFormItem label={"SLA"} {...formItemProps}>
                {renderCountdown()}
              </CustomFormItem>
            )}
            <CustomFormItem label={"Status"} {...formItemProps}>
              <Status type={data.status} />
            </CustomFormItem>
            {qaModuleSettings.preliminaryAnswers && (
              <CustomFormItem label={t("Ply. answers")} {...formItemProps}>
                {data.preliminaryAnswers > 0 ? <Tag>{data.preliminaryAnswers}</Tag> : "-"}
              </CustomFormItem>
            )}
            {(qaDetailsStore.data.actions?.delegate || qaDetailsStore.data.delegatedTo) && (
              <CustomFormItem label={t("Assignee")} {...formItemProps}>
                <QuestionDelegatePicker />
              </CustomFormItem>
            )}
          </Col>
        </Row>
        <div>
          <QAQuestion
            question={data.question}
            askedBy={data.askedBy}
            organisation={data.organisation}
            created={+data.created}
            canFetch={qaDetailsStore.data.actions?.comment}
            attachments={data.attachments}
            questionId={+params.id}
          />
          {qaDetailsStore.data.actions?.readAnswers && (
            <QAAnswers
              isNotInAllQuestionsPage={isNotInAllQuestionsPage}
              isSpecialist={qaModuleSettings.specialist || qaModuleSettings.delegatedTo}
              isApproval1={qaModuleSettings.approvalStage_1}
              isApproval2={qaModuleSettings.approvalStage_2}
              isCategoryApproval={qaModuleSettings.categoryApproval}
              canFetchComments={qaDetailsStore.data.actions?.comment}
              questionId={qaDetailsStore.questionId}
              role={QA_LINK_TO_ROLE_MAP[params.filter]}
              navigateBack={handleBackLink}
              canDeclineAnswer={qaDetailsStore.data.actions.declineAnswer}
              canAcceptAnswer={qaDetailsStore.data.actions.acceptAnswer}
            />
          )}
        </div>
      </div>
    </Spin>
  );
});
